import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';

const AppointmentBooking = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    citizenName: '',
    citizenId: '',
    phoneNumber: '',
    lineId: '',
    permitType: '',
    firearmSubType: '',
    appointmentDate: '',
    timeSlot: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const permitTypes = [
    { value: 'firearm', label: 'อาวุธปืน' },
    { value: 'hotel', label: 'โรงแรม' },
    { value: 'gambling', label: 'การพนัน' }
  ];

  const firearmSubTypes = [
    { value: 'p3_purchase_permit', label: 'การขออนุญาตมีและใช้อาวุธปืนและเครื่องกระสุนปืน (ออกใบอนุญาตซื้อ ป.3)' },
    { value: 'p4_possession_permit', label: 'การออกใบอนุญาตให้มีและใช้อาวุธปืนและเครื่องกระสุนปืน (ป.4)' },
    { value: 'p4_transfer_endorse', label: 'การขอสลักหลังตัดโอนใบอนุญาตให้มีและใช้อาวุธปืนและเครื่องกระสุนปืน (ป.4)' },
    { value: 'other', label: 'อื่นๆ' }
  ];

  const timeSlots = [
    { value: '09:00-10:00', label: '09:00 - 10:00' },
    { value: '10:00-11:00', label: '10:00 - 11:00' },
    { value: '11:00-12:00', label: '11:00 - 12:00' },
    { value: '13:00-14:00', label: '13:00 - 14:00' },
    { value: '14:00-15:00', label: '14:00 - 15:00' }
  ];

  // ดึงข้อมูลเวลาที่ถูกจองแล้วในวันที่เลือก
  const fetchBookedSlots = async (date) => {
    if (!date) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('time_slot')
        .eq('appointment_date', date)
        .eq('status', 'confirmed');

      if (error) {
        console.error('Error fetching booked slots:', error);
        return;
      }

      setBookedSlots(data.map(item => item.time_slot));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // เมื่อเปลี่ยนวันที่ ให้ดึงข้อมูลเวลาที่จองแล้ว
  useEffect(() => {
    if (formData.appointmentDate) {
      fetchBookedSlots(formData.appointmentDate);
    }
  }, [formData.appointmentDate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.citizenName.trim()) {
      newErrors.citizenName = 'กรุณากรอกชื่อ-สกุล';
    }

    if (!formData.citizenId.trim()) {
      newErrors.citizenId = 'กรุณากรอกเลขบัตรประชาชน';
    } else if (!/^[0-9]{13}$/.test(formData.citizenId.replace(/[-\s]/g, ''))) {
      newErrors.citizenId = 'เลขบัตรประชาชนต้องมี 13 หลัก';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'กรุณากรอกหมายเลขโทรศัพท์';
    } else if (!/^[0-9]{9,10}$/.test(formData.phoneNumber.replace(/[-\s]/g, ''))) {
      newErrors.phoneNumber = 'หมายเลขโทรศัพท์ไม่ถูกต้อง (9-10 หลัก)';
    }

    if (!formData.lineId.trim()) {
      newErrors.lineId = 'กรุณากรอกไอดีไลน์';
    }

    if (!formData.permitType) {
      newErrors.permitType = 'กรุณาเลือกประเภทการขออนุญาต';
    }

    if (formData.permitType === 'firearm' && !formData.firearmSubType) {
      newErrors.firearmSubType = 'กรุณาเลือกหัวข้อย่อยของอาวุธปืน';
    }

    if (!formData.appointmentDate) {
      newErrors.appointmentDate = 'กรุณาเลือกวันที่';
    }

    if (!formData.timeSlot) {
      newErrors.timeSlot = 'กรุณาเลือกช่วงเวลา';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const generateBookingNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `APT${year}${month}${day}${random}`;
  };

  const sendLineNotify = async (bookingData) => {
    try {
      const response = await fetch('https://notify-api.line.me/api/notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${import.meta.env.VITE_LINE_NOTIFY_TOKEN}`
        },
        body: new URLSearchParams({
          message: `📢 มีการจองคิวใหม่
ชื่อ: ${bookingData.citizen_name}
ประเภท: ${getPermitTypeLabel(bookingData.permit_type)}
วันที่: ${new Date(bookingData.appointment_date).toLocaleDateString('th-TH')}
เวลา: ${bookingData.time_slot}`
        })
      });

      if (!response.ok) {
        console.error('LINE Notify failed:', response.statusText);
      }
    } catch (error) {
      console.error('LINE Notify error:', error);
    }
  };

  const getPermitTypeLabel = (type) => {
    const permit = permitTypes.find(p => p.value === type);
    return permit ? permit.label : type;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const bookingNumber = generateBookingNumber();
      
      const { data, error } = await supabase
        .from('appointments')
        .insert({
          booking_number: bookingNumber,
          citizen_name: formData.citizenName.trim(),
          citizen_id: formData.citizenId.trim(),
          phone_number: formData.phoneNumber.trim(),
          line_id: formData.lineId.trim(),
          permit_type: formData.permitType,
          firearm_sub_type: formData.firearmSubType || null,
          appointment_date: formData.appointmentDate,
          time_slot: formData.timeSlot,
          status: 'confirmed',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating appointment:', error);
        throw new Error('เกิดข้อผิดพลาดในการจองคิว');
      }

      // ส่ง LINE Notify
      await sendLineNotify(data);

      setBookingResult({
        bookingNumber: data.booking_number,
        citizenName: data.citizen_name,
        permitType: getPermitTypeLabel(data.permit_type),
        appointmentDate: new Date(data.appointment_date).toLocaleDateString('th-TH'),
        timeSlot: data.time_slot
      });

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      citizenName: '',
      citizenId: '',
      phoneNumber: '',
      lineId: '',
      permitType: '',
      firearmSubType: '',
      appointmentDate: '',
      timeSlot: ''
    });
    setErrors({});
    setBookedSlots([]);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    resetForm();
    navigate('/public-services-portal');
  };

  // กรองเวลาที่ว่าง
  const availableTimeSlots = timeSlots.filter(slot => 
    !bookedSlots.includes(slot.value)
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={24} color="white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                จองคิวงานขออนุญาตฝ่ายปกครอง
              </h1>
              <p className="text-muted-foreground mt-1">
                ระบบจองคิวออนไลน์ อำเภอเทิง
              </p>
            </div>
          </div>
          
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-accent-foreground">
                  <strong>หมายเหตุ:</strong> กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง 
                  และเลือกวันที่และเวลาที่สะดวกในการเข้ารับบริการ
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-card rounded-lg border border-border shadow-government p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <Icon name="User" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                ข้อมูลผู้จองคิว
              </h2>
              <p className="text-sm text-muted-foreground">
                กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="ชื่อ-สกุล"
                type="text"
                placeholder="กรอกชื่อ-สกุลของท่าน"
                value={formData.citizenName}
                onChange={(e) => handleInputChange('citizenName', e.target.value)}
                error={errors.citizenName}
                required
              />

              <Input
                label="เลขบัตรประชาชน"
                type="text"
                placeholder="1234567890123"
                value={formData.citizenId}
                onChange={(e) => handleInputChange('citizenId', e.target.value)}
                error={errors.citizenId}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="หมายเลขโทรศัพท์"
                type="tel"
                placeholder="08X-XXX-XXXX"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                error={errors.phoneNumber}
                required
              />

              <Input
                label="ไอดีไลน์"
                type="text"
                placeholder="กรอกไอดีไลน์ของท่าน"
                value={formData.lineId}
                onChange={(e) => handleInputChange('lineId', e.target.value)}
                error={errors.lineId}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="ประเภทการขออนุญาต"
                placeholder="เลือกประเภทการขออนุญาต"
                options={permitTypes}
                value={formData.permitType}
                onChange={(val) => handleInputChange('permitType', val)}
                error={errors.permitType}
                required
              />
              {formData.permitType === 'firearm' && (
                <Select
                  label="หัวข้อย่อย (อาวุธปืน)"
                  placeholder="เลือกรายการ"
                  options={firearmSubTypes}
                  value={formData.firearmSubType}
                  onChange={(val) => handleInputChange('firearmSubType', val)}
                  error={errors.firearmSubType}
                  required
                />
              )}
            </div>

            {/* Appointment Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                รายละเอียดการนัดหมาย
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    วันที่นัดหมาย
                    <span className="text-error ml-1">*</span>
                  </label>
                  <input
                    type="date"
                    className={`w-full rounded-md border bg-background px-3 py-2 text-sm outline-none transition-colors ${
                      errors.appointmentDate ? 'border-error focus:border-error' : 'border-border focus:border-primary/60'
                    }`}
                    value={formData.appointmentDate}
                    onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  {errors.appointmentDate && (
                    <p className="text-xs text-error mt-1">{errors.appointmentDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    ช่วงเวลา
                    <span className="text-error ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {timeSlots.map((slot) => {
                      const isBooked = bookedSlots.includes(slot.value);
                      const isSelected = formData.timeSlot === slot.value;
                      
                      return (
                        <button
                          key={slot.value}
                          type="button"
                          disabled={isBooked}
                          onClick={() => handleInputChange('timeSlot', slot.value)}
                          className={`p-3 rounded-md border text-sm transition-colors ${
                            isBooked
                              ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                              : isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-background hover:border-primary/60'
                          }`}
                        >
                          {slot.label}
                          {isBooked && <span className="ml-2 text-xs">(จองแล้ว)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {errors.timeSlot && (
                    <p className="text-xs text-error mt-1">{errors.timeSlot}</p>
                  )}
                </div>
              </div>

              {loading && (
                <div className="text-center py-4">
                  <div className="inline-flex items-center space-x-2 text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>กำลังตรวจสอบเวลาที่ว่าง...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Calendar"
                iconPosition="left"
                className="sm:order-2"
                fullWidth
              >
                {isSubmitting ? 'กำลังจองคิว...' : 'จองคิว'}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                iconName="RotateCcw"
                iconPosition="left"
                className="sm:order-1"
                fullWidth
              >
                ล้างข้อมูล
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && bookingResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-1">
          <div className="bg-card rounded-lg border border-border shadow-md max-w-[90vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[45vw] xl:max-w-[35vw] max-h-[85vh] overflow-y-auto">
            <div className="p-2 sm:p-3">
              <div className="text-center mb-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                  <Icon name="CheckCircle" size={12} color="#10b981" />
                </div>
                <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-0.5">
                  จองคิวสำเร็จ!
                </h3>
                <p className="text-muted-foreground text-[9px] sm:text-[10px]">
                  การจองคิวของคุณได้รับการยืนยันแล้ว
                </p>
              </div>

              <div className="bg-muted/30 rounded-md p-1 sm:p-1.5 space-y-0.5">
                <div className="flex justify-between py-0.5">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground">เลขที่จอง:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium bg-blue-50 px-1 py-0.5 rounded">{bookingResult.bookingNumber}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground">ชื่อ-สกุล:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium">{bookingResult.citizenName}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground">ประเภท:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium">{bookingResult.permitType}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground">วันที่:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium">{bookingResult.appointmentDate}</span>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground">เวลา:</span>
                  <span className="text-[9px] sm:text-[10px] font-medium">{bookingResult.timeSlot}</span>
                </div>
              </div>

              <div className="flex justify-center pt-1 sm:pt-1.5">
                <Button onClick={handleSuccessModalClose} size="sm" className="text-[9px] sm:text-[10px] py-0.5 px-2 h-auto min-h-0">
                  ปิด
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentBooking;
